/**
 * Created by piotr.pozniak@thebeaverhead.com on 13/10/2018.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import classnames from "classnames";
import SelectorGroup from "./Selectors/SelectorGroup";
import MonthSelector from "./CalendarHeader/MonthSelector";
import CalendarHeaderLabel from "./CalendarHeader/CalendarHeaderLabel";
import IntegrationContext from "../strategies/IntegrationContext";
import { useFiltersStore } from "../../../hooks/redux/filters";
import LoadingIndicator from "./LoadingIndicator";
import CalendarHeaderSelectors from "./CalendarHeaderSelectors";
import WidgetFilterLabels from "./CalendarHeader/WidgetFilterLabels";
import useHeaderFilters from "../../../hooks/useHeaderFilters";
import WidgetSettingsContext from "../../../contexts/WidgetSettingsContext";
import ShareCalendarButton from "./ShareCalendarButton";
import SearchBar from "./SearchBar";
import { calendarPublicName } from "../helpers";
import SubscribeCalendarButton from "./SubscribeCalendarButton";
import { getPublicWidgetUrl } from "../../../helpers/url";
import { useTranslation } from "react-i18next";

/**
 * @param {Object} props
 * @param {Object} props.currentDate - currently selected date
 * @param {Array} props.events - events collection for the current view
 * @param {Object} props.calendar - calendar store object
 * @param {Boolean} [props.showMonthSelector=true] - show the month label/navigation
 * @param {function} [props.onUpcomingNavigationChange] - callback for upcoming-events pagination
 * @param {Object} props.selectedFilters - currently selected filters
 * @param {function} props.onChangeDate - callback when the selected month changes
 * @param {function} [props.onChangeFilter] - callback when a filter selection changes
 * @param {function} [props.onSearch] - callback for keyword search
 */
const CalendarHeader = ({
  currentDate,
  events,
  calendar,
  showMonthSelector = true,
  onUpcomingNavigationChange,
  selectedFilters,
  onChangeDate,
  onChangeFilter,
  onSearch,
}) => {
  const [state, setState] = useState({
    showFilters: false,
  });

  const { t } = useTranslation();
  const { fetchCalendarFilters, filters } = useFiltersStore();

  const widgetSettings = useContext(WidgetSettingsContext);
  const integrationStrategy = useContext(IntegrationContext);

  /**
   *
   */
  const toggleFilters = useCallback(() => {
    setState({ showFilters: !state.showFilters });
  }, [state.showFilters]);

  useHeaderFilters(
    integrationStrategy,
    widgetSettings,
    fetchCalendarFilters,
    calendar.model.uuid,
    state.showFilters,
    [integrationStrategy.availableFilters, state.showFilters]
  );

  const showMonthHeader = widgetSettings["showMonthHeader"] === 1;

  const kindOfNumberOfEvents = widgetSettings["eventsNumberOption"] || "all";

  const showPaginationNavigation =
    widgetSettings["showPaginationNavigation"] === 1;

  const showNavigation =
    ["upcoming", "all"].indexOf(kindOfNumberOfEvents) >= 0 &&
    showPaginationNavigation;

  const showUpcomingNavigationValue =
    widgetSettings["showUpcomingNavigation"] === 1;

  const showUpcomingNavigation =
    showUpcomingNavigationValue && kindOfNumberOfEvents == "upcoming-limit";

  const navigation = showNavigation ? (
    <MonthSelector onChangeDate={onChangeDate} />
  ) : null;

  const upcomingNavigation = showUpcomingNavigation ? (
    <MonthSelector onChangeDate={onUpcomingNavigationChange} />
  ) : null;

  const hasSelectors = useMemo(
    () =>
      integrationStrategy.availableFilters
        .map((kind) => {
          const showSelector = widgetSettings[`show${kind}Dropdown`];

          if (showSelector) {
            return 1;
          }
          return 0;
        })
        .reduce((a, b) => a + b, 0) > 0,
    [integrationStrategy.availableFilters, widgetSettings]
  );

  const expandFilters = widgetSettings["filterExpanded"] === 1;
  const showFilterToggle = widgetSettings["filterToggleShow"] === 1;

  useEffect(() => {
    setState({ showFilters: expandFilters && hasSelectors });
  }, [expandFilters, hasSelectors]);

  const showFilterLabel =
    hasSelectors && widgetSettings["showFiltersLabel"] === 1;
  const filterLabelText =
    hasSelectors && showFilterLabel ? widgetSettings["headerFiltersLabel"] : "";

  /**
   * If it is a single event, means that we cannot display selected date which is current date by default.
   * It may cause a wrong month header label display. E.g. Event happens in July and current month is June.
   * This way it will show June until events are loaded. If it's a single event, wait for that event to load
   * before displaying header.
   * @type {JSX.Element|null}
   */
  const monthSelector = showMonthSelector ? (
    <div className="month-selector">
      <CalendarHeaderLabel
        currentDate={currentDate}
        show={showMonthHeader}
        events={events}
        eventsNumberOption={kindOfNumberOfEvents}
        showUpcomingNavigation={showUpcomingNavigation}
      />

      {navigation}
      {upcomingNavigation}
    </div>
  ) : null;

  const filterLabel = showFilterLabel ? (
    <div className={classnames("filter_title")}>{filterLabelText}</div>
  ) : null;

  const filtersToggleButton =
    showFilterToggle && hasSelectors ? (
      <>
        <button
          type="button"
          className={classnames(
            "dce-btn-reset",
            "month-selector--filters-button",
            {
              active: state.showFilters || expandFilters,
            }
          )}
          onClick={toggleFilters}
          aria-label={t("Toggle filters")}
          aria-expanded={state.showFilters}
        >
          <i className="material-icons" aria-hidden="true">
            filter_list
          </i>
        </button>
        {filterLabel}
      </>
    ) : null;

  const loadingIndicator = filters.fetchCalendarFilters ? (
    <div className={"loading-indicator"}>
      <LoadingIndicator />
    </div>
  ) : null;

  const selectors =
    state.showFilters && !loadingIndicator ? (
      <CalendarHeaderSelectors
        onChangeFilter={onChangeFilter}
        selectedFilters={selectedFilters}
      />
    ) : null;

  return (
    <div>
      <WidgetFilterLabels calendar={calendar} />

      <div className={"dc-calendar--filter-share-container"}>
        <div className={"month-selector--filters-controls"}>
          {filtersToggleButton}
          <SearchBar onSearch={onSearch} />
        </div>

        <div className={"dc-calendar--header-features-container"}>
          <SubscribeCalendarButton
            title={calendarPublicName(calendar.model)}
            url={getPublicWidgetUrl(`c/${calendar.model.slug}`)}
            RSSUrl={`${process.env.BASE_URL}/feeds/calendar/${calendar.model.uuid}.rss`}
            iCalPath={`${calendar.model.integration.uuid}/${calendar.model.uuid}`}
          />
          <ShareCalendarButton
            title={calendarPublicName(calendar.model)}
            url={getPublicWidgetUrl(
              `c/${calendar.model.slug}`,
              calendar.model.widget_domain
            )}
          />
        </div>
      </div>

      {monthSelector}
      {""}
      <SelectorGroup show={state.showFilters}>
        {loadingIndicator}
        {selectors}
      </SelectorGroup>
    </div>
  );
};

export default CalendarHeader;
